'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { VariantProps } from 'tailwind-variants';

import { IconKitArrow } from '@/icon/kit/Arrow';
import type { JsonLdBreadcrumbItem } from '@/util/map-to-json-ld';

import { styles } from './UiKitBreadcrumbs.styles';

type NativeElement = React.HTMLAttributes<HTMLDivElement>;

export type BreadcrumbsItem = JsonLdBreadcrumbItem;

export interface UiKitBreadcrumbsProps {
  className?: string;
  items: BreadcrumbsItem[];
  onClick?: NativeElement['onClick'];
  small?: VariantProps<typeof styles>['small'];
  smallIsBack?: boolean;
  margin?: boolean;
}

export function UiKitBreadcrumbs({
  className,
  items,
  onClick,
  small,
  smallIsBack,
  margin
}: UiKitBreadcrumbsProps) {
  const slots = styles({ small, margin });
  const t = useTranslations();
  const lastItem = items[items.length - 2];

  const withTrailingSlash = (href: string) => (href.endsWith('/') ? href : `${href}/`);

  return (
    <nav
      className={slots.base({ className })}
      onClick={onClick}
    >
      <ol className={slots.list()}>
        {items.map((item, index) => (
          <li
            key={index}
            className={slots.item()}
          >
            {index > 0 && <span className={slots.separator()}>/</span>}
            {item.href && index < items.length - 1 ? (
              <Link
                href={withTrailingSlash(item.href)}
                className={slots.link()}
                dangerouslySetInnerHTML={{ __html: item.label }}
              />
            ) : (
              <span dangerouslySetInnerHTML={{ __html: item.label }} />
            )}
          </li>
        ))}
      </ol>
      {lastItem && (
        <div className={slots.mobile()}>
          {lastItem.href ? (
            <Link
              href={withTrailingSlash(lastItem.href)}
              className={slots.link()}
            >
              <IconKitArrow className={slots.arrow()} />
              {smallIsBack ? t('back') : lastItem.label}
            </Link>
          ) : (
            <div className={slots.link()}>{smallIsBack ? t('back') : lastItem.label}</div>
          )}
        </div>
      )}
    </nav>
  );
}
